/**
 * Sentry Breadcrumb Helpers
 *
 * Custom breadcrumb utilities for tracking user actions
 * and providing context for error debugging.
 */

import * as Sentry from '@sentry/react';

type BreadcrumbLevel = 'debug' | 'info' | 'warning' | 'error';

/**
 * Add a navigation breadcrumb
 */
export function addNavigationBreadcrumb(from: string, to: string) {
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `${from} -> ${to}`,
    level: 'info',
  });
}

/**
 * Add an API call breadcrumb
 */
export function addApiBreadcrumb(
  method: string,
  url: string,
  statusCode?: number,
  level: BreadcrumbLevel = 'info'
) {
  Sentry.addBreadcrumb({
    category: 'api',
    message: `${method} ${url}`,
    level,
    data: statusCode ? { statusCode } : undefined,
  });
}

/**
 * Add a user action breadcrumb
 */
export function addUserActionBreadcrumb(action: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    category: 'user',
    message: action,
    level: 'info',
    data,
  });
}

/**
 * Add a staging operation breadcrumb
 */
export function addStagingBreadcrumb(
  action: 'start' | 'complete' | 'error',
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    category: 'staging',
    message: `Staging ${action}`,
    level: action === 'error' ? 'error' : 'info',
    data,
  });
}

/**
 * Add a payment operation breadcrumb
 */
export function addPaymentBreadcrumb(action: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    category: 'payment',
    message: action,
    level: 'info',
    data,
  });
}

/**
 * Add a custom breadcrumb
 */
export function addBreadcrumb(
  category: string,
  message: string,
  level: BreadcrumbLevel = 'info',
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data,
  });
}
